import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout7Root } from './Layout7Root';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { FeaturedListingCard } from './FeaturedListingCard';
import { InventoryFilterBar } from './InventoryFilterBar';
import { InventoryFiltersDrawer } from './InventoryFiltersDrawer';
import { VehicleFilters } from '@/components/VehicleFilters';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { useAppData, useFilteredVehicles } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import {
  buildDefaultFilters,
  filterVehicles,
  filtersToSearchParams,
  hasActiveFilters,
  hasAdvancedFilters,
  buildFacetOptions,
  sanitizeFilterSelections,
  sortVehicles,
  uniqueSorted,
  vehiclesForFacet,
  type FilterRanges,
  type VehicleFilterState,
} from './inventoryFilters';
import './inventory.css';
import './styles.css';

const DRAWER_HIDDEN_SECTIONS = ['type', 'brand', 'model'];

const VehiclesListing = () => {
  const { config } = useAppData();
  const { colors } = useTheme();
  const { selectedStore } = useStore();
  const contextVehicles = useFilteredVehicles(selectedStore);
  const allStores = config?.stores || [];
  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const titleColor = colors.title;
  const textColor = colors.text;
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [sortBy, setSortBy] = useState('price-asc');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    brand: true,
    year: true,
    price: true,
    mileage: true,
    fuel: true,
    category: true,
    transmission: true,
    color: true,
    optionals: true,
    type: true,
    store: true,
  });

  const [displayedCount, setDisplayedCount] = useState(12);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const itemsPerLoad = 12;
  const maxDisplayedItems = 100;
  const observerTarget = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const ranges = useMemo<FilterRanges>(() => {
    if (contextVehicles.length === 0) {
      return {
        minPrice: 0,
        maxPrice: 500000,
        minYear: 2000,
        maxYear: 2024,
        minMileage: 0,
        maxMileage: 200000,
      };
    }

    const minModelYear = Math.min(...contextVehicles.map((v) => v.modelYear));
    const maxModelYear = Math.max(...contextVehicles.map((v) => v.modelYear));

    return {
      minPrice: Math.min(...contextVehicles.map((v) => v.price)),
      maxPrice: Math.max(...contextVehicles.map((v) => v.price)),
      minYear: minModelYear - 1,
      maxYear: maxModelYear,
      minMileage: Math.min(...contextVehicles.map((v) => v.mileage)),
      maxMileage: Math.max(...contextVehicles.map((v) => v.mileage)),
    };
  }, [contextVehicles]);

  const [appliedFilters, setAppliedFilters] = useState<VehicleFilterState>(() =>
    buildDefaultFilters(ranges, searchParams)
  );
  const [pendingFilters, setPendingFilters] = useState<VehicleFilterState>(() =>
    buildDefaultFilters(ranges, searchParams)
  );

  const updatePending = useCallback((patch: Partial<VehicleFilterState>) => {
    setPendingFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const setPendingType = useCallback((value: string) => {
    setPendingFilters((prev) => ({
      ...prev,
      selectedType: value,
      selectedBrand: 'all',
      selectedModel: 'all',
    }));
  }, []);

  const setPendingBrand = useCallback((value: string) => {
    setPendingFilters((prev) => ({
      ...prev,
      selectedBrand: value,
      selectedModel: 'all',
    }));
  }, []);

  // Facets dependentes: cada lista só mostra o que ainda existe com os demais filtros
  const brands = useMemo(
    () =>
      uniqueSorted(
        vehiclesForFacet(contextVehicles, pendingFilters, ranges, ['brand', 'model'], searchParams).map(
          (v) => v.brand
        )
      ),
    [contextVehicles, pendingFilters, ranges, searchParams]
  );

  const models = useMemo(
    () =>
      uniqueSorted(
        vehiclesForFacet(contextVehicles, pendingFilters, ranges, 'model', searchParams).map(
          (v) => v.model
        )
      ),
    [contextVehicles, pendingFilters, ranges, searchParams]
  );

  const availableTypes = useMemo(() => {
    const pool = vehiclesForFacet(contextVehicles, pendingFilters, ranges, 'type', searchParams);
    const types: string[] = [];
    if (pool.some((v) => v.isZeroKm || v.mileage === 0)) types.push('zerokm');
    if (pool.some((v) => !v.isZeroKm && v.mileage > 0)) types.push('seminovo');
    return types;
  }, [contextVehicles, pendingFilters, ranges, searchParams]);

  const fuels = useMemo(
    () =>
      uniqueSorted(
        vehiclesForFacet(contextVehicles, pendingFilters, ranges, 'fuels', searchParams).map(
          (v) => v.fuel
        )
      ),
    [contextVehicles, pendingFilters, ranges, searchParams]
  );

  const transmissions = useMemo(
    () =>
      uniqueSorted(
        vehiclesForFacet(contextVehicles, pendingFilters, ranges, 'transmissions', searchParams).map(
          (v) => v.transmission
        )
      ),
    [contextVehicles, pendingFilters, ranges, searchParams]
  );

  const vehicleColors = useMemo(
    () =>
      uniqueSorted(
        vehiclesForFacet(contextVehicles, pendingFilters, ranges, 'colors', searchParams).map(
          (v) => v.color
        )
      ),
    [contextVehicles, pendingFilters, ranges, searchParams]
  );

  const optionals = useMemo(() => {
    const pool = vehiclesForFacet(contextVehicles, pendingFilters, ranges, 'optionals', searchParams);
    return uniqueSorted(pool.flatMap((v) => v.optionals || []));
  }, [contextVehicles, pendingFilters, ranges, searchParams]);

  const categories = useMemo(() => {
    const pool = vehiclesForFacet(contextVehicles, pendingFilters, ranges, 'category', searchParams);
    const uniqueTypes = Array.from(new Set(pool.map((v) => v.type)));
    return uniqueTypes
      .map((type) => {
        switch (type) {
          case 1:
            return { value: '1', label: 'Carro' };
          case 2:
            return { value: '2', label: 'Moto' };
          case 3:
            return { value: '3', label: 'Caminhão' };
          default:
            return null;
        }
      })
      .filter(Boolean)
      .sort((a, b) => a!.value.localeCompare(b!.value)) as { value: string; label: string }[];
  }, [contextVehicles, pendingFilters, ranges, searchParams]);

  // Limpa marca/modelo/etc. inválidos quando o facet pai muda (ex.: Seminovos → BYD some)
  useEffect(() => {
    setPendingFilters((prev) => {
      const next = sanitizeFilterSelections(prev, {
        brands,
        models,
        fuels,
        transmissions,
        colors: vehicleColors,
        optionals,
        categories: categories.map((c) => c.value),
        types: availableTypes,
      });
      return next === prev || JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [brands, models, fuels, transmissions, vehicleColors, optionals, categories, availableTypes]);

  useEffect(() => {
    setAppliedFilters((prev) => ({
      ...prev,
      priceRange: [
        Math.max(prev.priceRange[0], ranges.minPrice),
        Math.min(prev.priceRange[1], ranges.maxPrice),
      ],
      yearRange: [
        Math.max(prev.yearRange[0], ranges.minYear),
        Math.min(prev.yearRange[1], ranges.maxYear),
      ],
      mileageRange: [
        Math.max(prev.mileageRange[0], ranges.minMileage),
        Math.min(prev.mileageRange[1], ranges.maxMileage),
      ],
    }));
    setPendingFilters((prev) => ({
      ...prev,
      priceRange: [
        Math.max(prev.priceRange[0], ranges.minPrice),
        Math.min(prev.priceRange[1], ranges.maxPrice),
      ],
      yearRange: [
        Math.max(prev.yearRange[0], ranges.minYear),
        Math.min(prev.yearRange[1], ranges.maxYear),
      ],
      mileageRange: [
        Math.max(prev.mileageRange[0], ranges.minMileage),
        Math.min(prev.mileageRange[1], ranges.maxMileage),
      ],
    }));
  }, [
    ranges.minPrice,
    ranges.maxPrice,
    ranges.minYear,
    ranges.maxYear,
    ranges.minMileage,
    ranges.maxMileage,
  ]);

  useEffect(() => {
    const fromUrl = buildDefaultFilters(ranges, searchParams);
    const sanitized = sanitizeFilterSelections(
      fromUrl,
      buildFacetOptions(contextVehicles, fromUrl, ranges, searchParams)
    );
    setAppliedFilters(sanitized);
    setPendingFilters(sanitized);

    // Remove da URL combinações sem estoque (ex.: type=seminovo&brand=BYD)
    if (JSON.stringify(sanitized) !== JSON.stringify(fromUrl)) {
      const params = filtersToSearchParams(sanitized);
      setSearchParams(params.toString() ? params : new URLSearchParams(), { replace: true });
    }
  }, [searchParams, ranges, contextVehicles, setSearchParams]);

  const previewVehicles = useMemo(
    () => sortVehicles(filterVehicles(contextVehicles, pendingFilters, searchParams, ranges), sortBy),
    [contextVehicles, pendingFilters, searchParams, ranges, sortBy]
  );

  const filteredVehicles = useMemo(
    () => sortVehicles(filterVehicles(contextVehicles, appliedFilters, searchParams, ranges), sortBy),
    [contextVehicles, appliedFilters, searchParams, ranges, sortBy]
  );

  useEffect(() => {
    setDisplayedCount(12);
    setIsLoadingMore(false);
  }, [filteredVehicles]);

  const loadMore = useCallback(() => {
    if (isLoadingMore) return;

    setIsLoadingMore(true);
    setDisplayedCount((prev) =>
      Math.min(prev + itemsPerLoad, filteredVehicles.length, maxDisplayedItems)
    );
    requestAnimationFrame(() => {
      setIsLoadingMore(false);
    });
  }, [isLoadingMore, filteredVehicles.length]);

  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    if (displayedCount >= Math.min(filteredVehicles.length, maxDisplayedItems)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(currentTarget);

    return () => {
      observer.unobserve(currentTarget);
    };
  }, [displayedCount, filteredVehicles.length, loadMore]);

  const clearPendingFilters = useCallback(() => {
    setPendingFilters(buildDefaultFilters(ranges));
  }, [ranges]);

  const clearAllFilters = useCallback(() => {
    const defaults = buildDefaultFilters(ranges);
    setAppliedFilters(defaults);
    setPendingFilters(defaults);
    setSearchParams(new URLSearchParams());
  }, [ranges, setSearchParams]);

  const facetOptions = useMemo(
    () => ({
      brands,
      models,
      fuels,
      transmissions,
      colors: vehicleColors,
      optionals,
      categories: categories.map((c) => c.value),
      types: availableTypes,
    }),
    [
      brands,
      models,
      fuels,
      transmissions,
      vehicleColors,
      optionals,
      categories,
      availableTypes,
    ]
  );

  const applyFilters = useCallback(() => {
    const sanitized = sanitizeFilterSelections(pendingFilters, facetOptions);
    setPendingFilters(sanitized);
    setAppliedFilters(sanitized);
    const params = filtersToSearchParams(sanitized);
    setSearchParams(params.toString() ? params : new URLSearchParams());
    setDisplayedCount(12);
  }, [pendingFilters, facetOptions, setSearchParams]);

  const scrollToResultsIfNeeded = useCallback(() => {
    const resultsEl = resultsRef.current;
    if (!resultsEl) return;
    if (resultsEl.getBoundingClientRect().top > window.innerHeight * 0.4) {
      resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleApplyFromDrawer = useCallback(() => {
    const sanitized = sanitizeFilterSelections(pendingFilters, facetOptions);
    setPendingFilters(sanitized);
    setAppliedFilters(sanitized);
    const params = filtersToSearchParams(sanitized);
    setSearchParams(params.toString() ? params : new URLSearchParams());
    setShowFiltersDrawer(false);
    setDisplayedCount(12);
    requestAnimationFrame(() => {
      scrollToResultsIfNeeded();
    });
  }, [pendingFilters, facetOptions, setSearchParams, scrollToResultsIfNeeded]);

  const handleDrawerOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setPendingFilters({ ...appliedFilters });
      } else {
        setPendingFilters({ ...appliedFilters });
      }
      setShowFiltersDrawer(open);
    },
    [appliedFilters]
  );

  const handleOpenMoreFilters = useCallback(() => {
    setPendingFilters({ ...appliedFilters });
    setShowFiltersDrawer(true);
  }, [appliedFilters]);

  const appliedHasActiveFilters = hasActiveFilters(appliedFilters, ranges);
  const pendingHasActiveFilters = hasActiveFilters(pendingFilters, ranges);
  const appliedHasAdvancedFilters = hasAdvancedFilters(appliedFilters, ranges);

  const displayedVehicles = filteredVehicles.slice(0, displayedCount);
  const totalCap = Math.min(filteredVehicles.length, maxDisplayedItems);
  const showingTo = Math.min(displayedCount, filteredVehicles.length);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const filterPropsBase = {
    searchTerm: pendingFilters.searchTerm,
    setSearchTerm: (value: string) => updatePending({ searchTerm: value }),
    selectedBrand: pendingFilters.selectedBrand,
    setSelectedBrand: setPendingBrand,
    selectedModel: pendingFilters.selectedModel,
    setSelectedModel: (value: string) => updatePending({ selectedModel: value }),
    selectedFuels: pendingFilters.selectedFuels,
    setSelectedFuels: (value: string[]) => updatePending({ selectedFuels: value }),
    selectedTransmissions: pendingFilters.selectedTransmissions,
    setSelectedTransmissions: (value: string[]) => updatePending({ selectedTransmissions: value }),
    selectedType: pendingFilters.selectedType,
    setSelectedType: setPendingType,
    selectedCategory: pendingFilters.selectedCategory,
    setSelectedCategory: (value: string) => updatePending({ selectedCategory: value }),
    selectedColors: pendingFilters.selectedColors,
    setSelectedColors: (value: string[]) => updatePending({ selectedColors: value }),
    selectedOptionals: pendingFilters.selectedOptionals,
    setSelectedOptionals: (value: string[]) => updatePending({ selectedOptionals: value }),
    selectedStores: pendingFilters.selectedStores,
    setSelectedStores: (value: string[]) => updatePending({ selectedStores: value }),
    priceRange: pendingFilters.priceRange,
    setPriceRange: (value: number[]) => updatePending({ priceRange: value as [number, number] }),
    yearRange: pendingFilters.yearRange,
    setYearRange: (value: number[]) => updatePending({ yearRange: value as [number, number] }),
    mileageRange: pendingFilters.mileageRange,
    setMileageRange: (value: number[]) => updatePending({ mileageRange: value as [number, number] }),
    config,
    brands,
    models,
    fuels,
    transmissions,
    categories,
    colors: vehicleColors,
    optionals,
    availableStores: allStores,
    selectedStore,
    vehicles: contextVehicles,
    ranges,
    clearFilters: clearPendingFilters,
    hasActiveFilters: pendingHasActiveFilters,
    filteredCount: previewVehicles.length,
    expandedSections,
    toggleSection,
    hideHeader: true,
    layout: 'drawer' as const,
  };

  const drawerFilterProps = {
    ...filterPropsBase,
    hiddenSections: DRAWER_HIDDEN_SECTIONS,
  };

  const sidebarFilterProps = {
    ...filterPropsBase,
    /* Tipo/Marca/Modelo ficam na barra superior (como a referência) */
    hiddenSections: DRAWER_HIDDEN_SECTIONS,
  };

  return (
    <Layout7Root withHeader>
      <Header config={config} />

      <div className="wl7-inv-page">
        <div className="wl7-inv-hero">
          <div className="wl7-inv-hero__bg" aria-hidden />
          <div className="wl7-container wl7-inv-hero__inner">
            <h1 className="wl7-inv-hero__title" style={{ color: titleColor }}>
              Estoque
            </h1>
            <p className="wl7-inv-hero__lead" style={{ color: textColor }}>
              Encontre o automóvel perfeito para você e sua família! Veículos revisados, com garantia e qualidade.
            </p>
          </div>
        </div>

        <div className="wl7-container pb-16">
          <div className="wl7-inv-layout">
            <aside className="wl7-inv-sidebar">
              <div className="wl7-inv-sidebar-card">
                <div className="wl7-inv-sidebar-search">
                  <input
                    type="search"
                    className="wl7-inv-sidebar-search__input"
                    placeholder="Buscar no estoque"
                    value={pendingFilters.searchTerm}
                    onChange={(e) => updatePending({ searchTerm: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applyFilters();
                    }}
                    aria-label="Buscar no estoque"
                  />
                  <button
                    type="button"
                    className="wl7-inv-sidebar-search__btn"
                    style={{ backgroundColor: buttonColor, color: 'var(--wl7-on-button, #fff)' }}
                    onClick={applyFilters}
                    aria-label="Buscar"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>

                <div className="wl7-inv-sidebar-sort">
                  <h3 className="wl7-inv-sidebar-sort__title">Ordenar por:</h3>
                  {[
                    { value: 'price-asc', label: 'Menor Preço' },
                    { value: 'price-desc', label: 'Maior Preço' },
                    { value: 'year-desc', label: 'Mais novo' },
                    { value: 'mileage-asc', label: 'Menor km' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`wl7-inv-sidebar-sort__row${sortBy === opt.value ? ' is-active' : ''}`}
                      onClick={() => setSortBy(opt.value)}
                    >
                      <span>{opt.label}</span>
                      <span
                        className="wl7-inv-sidebar-sort__toggle"
                        style={
                          sortBy === opt.value
                            ? { backgroundColor: buttonColor }
                            : undefined
                        }
                        aria-hidden
                      />
                    </button>
                  ))}
                </div>

                <div className="wl7-inv-sidebar-head">
                  <h2 className="wl7-inv-sidebar-title">Filtros</h2>
                  {pendingHasActiveFilters && (
                    <button type="button" className="wl7-inv-sidebar-clear" onClick={clearPendingFilters}>
                      Resetar
                    </button>
                  )}
                </div>
                <VehicleFilters {...sidebarFilterProps} hideSearch hideClearAll />
                <Button
                  className="w-full mt-4 font-semibold"
                  style={{ background: buttonColor, color: 'var(--wl7-on-button, #fff)' }}
                  onClick={applyFilters}
                >
                  Aplicar ({previewVehicles.length})
                </Button>
              </div>
            </aside>

            <div className="wl7-inv-main">
              <div className="hidden lg:block">
                <InventoryFilterBar
                  selectedType={pendingFilters.selectedType}
                  setSelectedType={setPendingType}
                  selectedBrand={pendingFilters.selectedBrand}
                  setSelectedBrand={setPendingBrand}
                  selectedModel={pendingFilters.selectedModel}
                  setSelectedModel={(value) => updatePending({ selectedModel: value })}
                  brands={brands}
                  models={models}
                  availableTypes={availableTypes}
                  filteredCount={previewVehicles.length}
                  hasAdvancedFilters={appliedHasAdvancedFilters}
                  hasFilters={appliedHasActiveFilters || pendingHasActiveFilters}
                  buttonColor={buttonColor}
                  onOpenMoreFilters={handleOpenMoreFilters}
                  onClear={clearAllFilters}
                  onSearch={applyFilters}
                  variant="top"
                />
              </div>

              <div className="lg:hidden mb-4">
                <InventoryFilterBar
                  selectedType={pendingFilters.selectedType}
                  setSelectedType={setPendingType}
                  selectedBrand={pendingFilters.selectedBrand}
                  setSelectedBrand={setPendingBrand}
                  selectedModel={pendingFilters.selectedModel}
                  setSelectedModel={(value) => updatePending({ selectedModel: value })}
                  brands={brands}
                  models={models}
                  availableTypes={availableTypes}
                  filteredCount={previewVehicles.length}
                  hasAdvancedFilters={appliedHasAdvancedFilters}
                  hasFilters={appliedHasActiveFilters || pendingHasActiveFilters}
                  buttonColor={buttonColor}
                  onOpenMoreFilters={handleOpenMoreFilters}
                  onClear={clearAllFilters}
                  onSearch={applyFilters}
                  variant="mobile"
                />
              </div>

              <div ref={resultsRef} className="wl7-inv-results">
                <p className="wl7-inv-results__count">
                  <strong>{filteredVehicles.length}</strong>{' '}
                  {filteredVehicles.length === 1 ? 'veículo encontrado' : 'veículos encontrados'}
                  {filteredVehicles.length > 0 && (
                    <>
                      {' '}
                      · mostrando até <strong>{showingTo}</strong>
                    </>
                  )}
                </p>
              </div>

              {filteredVehicles.length > 0 ? (
                <>
                  <div className="wl7-inv-list">
                    {displayedVehicles.map((vehicle) => (
                      <FeaturedListingCard key={vehicle.id} vehicle={vehicle} variant="row" />
                    ))}
                  </div>

                  {displayedCount < totalCap && (
                    <div ref={observerTarget} className="wl7-inv-loading">
                      <div
                        className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
                        style={{
                          borderColor: `${primaryColor} transparent ${primaryColor} ${primaryColor}`,
                        }}
                      />
                      <p className="wl7-inv-loading__text">
                        Carregando mais veículos... ({displayedCount} de {totalCap})
                      </p>
                    </div>
                  )}

                  {displayedCount >= maxDisplayedItems &&
                  filteredVehicles.length > maxDisplayedItems ? (
                    <div className="wl7-inv-end">
                      <p className="wl7-inv-end__text">
                        Mostrando {maxDisplayedItems} de {filteredVehicles.length} veículos
                      </p>
                      <p className="wl7-inv-end__hint">
                        Use os filtros para refinar sua busca e ver resultados mais específicos
                      </p>
                    </div>
                  ) : displayedCount >= filteredVehicles.length && filteredVehicles.length > 12 ? (
                    <div className="wl7-inv-end">
                      <p className="wl7-inv-end__text">
                        Todos os {filteredVehicles.length} veículos foram carregados
                      </p>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="wl7-inv-empty">
                  <h2 className="wl7-inv-empty__title">Nenhum veículo encontrado</h2>
                  <p className="wl7-inv-empty__text">
                    Não encontramos veículos com os filtros selecionados. Tente ajustar sua busca.
                  </p>
                  <Button
                    onClick={clearAllFilters}
                    disabled={!appliedHasActiveFilters}
                    style={{ background: buttonColor }}
                  >
                    Limpar filtros
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <InventoryFiltersDrawer
        open={showFiltersDrawer}
        onOpenChange={handleDrawerOpenChange}
        searchTerm={pendingFilters.searchTerm}
        onSearchTermChange={(value) => updatePending({ searchTerm: value })}
        previewCount={previewVehicles.length}
        buttonColor={buttonColor}
        hasPendingFilters={pendingHasActiveFilters}
        onClearPending={clearPendingFilters}
        onApply={handleApplyFromDrawer}
        filterProps={drawerFilterProps}
      />

      <Footer config={config} />
    </Layout7Root>
  );
};

export default VehiclesListing;
